import { customEndpoint } from '@directus/sdk'
import directus from '@/lib/directus'
import { storage } from '@/lib/storage'

const PHONE_STATUS_KEY = 'phone_verification_status'
const PROMPT_OPENED_AT_KEY = 'phone_verification_prompt_opened_at'

export const normalizeE164 = (value: string) => value.replace(/\D/g, '').slice(0, 15)

export interface PhoneStatus {
  hasPhone: boolean
  phoneVerified?: boolean
  phoneNumber?: string
}

export interface InitiateOtpResponse {
  success: boolean
  token?: string
  expiresAt?: string
  error?: string
}

export interface VerifyOtpResponse {
  success: boolean
  phoneNumber?: string
  phoneVerified?: boolean
  error?: string
}

export const phoneVerificationService = {
  async getStatus(): Promise<PhoneStatus> {
    try {
      const response = await directus.request(customEndpoint({
        path: '/api/auth/phone/status',
        method: 'GET',
      }))
      const status = response as PhoneStatus
      storage.write(PHONE_STATUS_KEY, status)
      return status
    } catch (error: any) {
      console.error('Error fetching phone status:', error)
      return storage.read<PhoneStatus>(PHONE_STATUS_KEY) || { hasPhone: false }
    }
  },

  async initiateOtp(phoneNumber: string): Promise<InitiateOtpResponse> {
    try {
      const response = await directus.request(customEndpoint({
        path: '/api/auth/phone/initiate-otp',
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
      }))
      return response as InitiateOtpResponse
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || "Erreur lors de l'envoi du code",
      }
    }
  },

  async verifyOtp(token: string, otp: string): Promise<VerifyOtpResponse> {
    try {
      const response = await directus.request(customEndpoint({
        path: '/api/auth/phone/verify-otp',
        method: 'POST',
        body: JSON.stringify({ token, otp }),
      }))
      const result = response as VerifyOtpResponse
      if (result.success && result.phoneNumber) {
        storage.write(PHONE_STATUS_KEY, { hasPhone: true, phoneVerified: true, phoneNumber: result.phoneNumber })
      }
      return result
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || "Erreur lors de la vérification",
      }
    }
  },

  shouldShowPrompt(): boolean {
    const openedAt = storage.read<number>(PROMPT_OPENED_AT_KEY)
    if (!openedAt) return true

    const oneHour = 60 * 60 * 1000
    return Date.now() - openedAt < oneHour
  },

  markPromptShown(): void {
    storage.write(PROMPT_OPENED_AT_KEY, Date.now())
  },

  clearStatus(): void {
    storage.remove(PHONE_STATUS_KEY)
    storage.remove(PROMPT_OPENED_AT_KEY)
  }
}
